import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, MessageCircle, Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Collects the buyer's details so the order lands in the admin inbox as well as
   WhatsApp. Everything is optional — "Skip and send" still logs the order. */
export const OrderDetailsDialog = ({ open, onClose, items, totals, whatsappHref, onSent, buyer }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gstRegistered, setGstRegistered] = useState(null);
  const [gstNumber, setGstNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Prefill from the signed-in buyer, but leave anything they've already typed alone
  useEffect(() => {
    if (!open || !buyer) return;
    setName((v) => v || buyer.name || "");
    setPhone((v) => v || buyer.phone || "");
    if (buyer.gst_registered != null) {
      setGstRegistered((v) => (v === null ? buyer.gst_registered : v));
      setGstNumber((v) => v || buyer.gst_number || "");
    }
  }, [open, buyer]);

  if (!open) return null;

  const gstinValid = /^[0-9A-Z]{15}$/.test(gstNumber.trim().toUpperCase());
  const gstinMissing = gstRegistered === true && !gstinValid;

  const send = async (withDetails) => {
    setBusy(true);
    setError("");

    // Opened synchronously inside the click so iOS/in-app browsers don't block it
    const popup = window.open("", "_blank", "noopener,noreferrer");

    const payload = {
      items: items.map((i) => ({
        quality_id: i.quality_id,
        quality_name: i.quality_name,
        number: i.number ?? null,
        cut_length: i.cut_length ?? null,
        thans: i.thans,
        than_meters: i.than_meters ?? null,
        pieces_per_pack: i.pieces_per_pack ?? null,
        hex: i.hex ?? null,
      })),
      totals,
      ...(withDetails
        ? {
            name: name.trim() || null,
            phone: phone.trim() || null,
            gst_registered: gstRegistered,
            gst_number: gstRegistered ? gstNumber.trim().toUpperCase() || null : null,
            notes: notes.trim() || null,
          }
        : {}),
    };

    const handOff = () => {
      if (popup && !popup.closed) popup.location.href = whatsappHref;
      else window.open(whatsappHref, "_blank", "noopener,noreferrer");
    };

    try {
      const res = await fetch(`${API}/order-enquiries`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.status >= 400 && res.status < 500) {
        // Our fault to fix, not the shop's: keep the dialog open, send nothing
        const body = await res.json().catch(() => ({}));
        if (popup && !popup.closed) popup.close();
        setError(formatApiError(body.detail, "Please check the details and try again."));
        return;
      }
      if (!res.ok) throw new Error("save failed");

      const saved = await res.json();
      onSent(saved.reference);
      handOff();
      onClose();
    } catch (e) {
      // Network or server error — the order must still reach the shop
      console.error("Order save failed:", e);
      setError("We couldn't save a copy, but your order is opening in WhatsApp.");
      handOff();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ backgroundColor: "rgba(16,16,16,0.72)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Your details"
      data-testid="order-details-dialog"
    >
      <div
        className="w-full sm:max-w-md max-h-[90vh] overflow-y-auto border sm:rounded-sm"
        style={{ backgroundColor: "var(--mp-card)", borderColor: "var(--mp-line)" }}
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b sticky top-0"
          style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
        >
          <div>
            <p
              className="text-[10px] tracking-[0.18em] uppercase"
              style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
            >
              Almost there
            </p>
            <h2
              className="text-lg"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Your details
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="order-details-close"
            aria-label="Close"
            className="p-2 rounded-sm"
            style={{ color: "var(--mp-muted)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <Field label="Name" testid="order-name-field">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Shop or buyer name"
              data-testid="order-name-input"
              className="w-full text-sm bg-transparent outline-none px-3 py-2.5 rounded-sm border"
              style={{
                borderColor: "var(--mp-line)",
                color: "var(--mp-text)",
                fontFamily: "'Manrope', sans-serif",
              }}
            />
          </Field>

          <Field label="Mobile number" testid="order-phone-field">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              placeholder="10-digit mobile"
              data-testid="order-phone-input"
              className="w-full text-sm bg-transparent outline-none px-3 py-2.5 rounded-sm border"
              style={{
                borderColor: "var(--mp-line)",
                color: "var(--mp-text)",
                fontFamily: "'Manrope', sans-serif",
              }}
            />
            <p
              className="mt-1 text-[11px]"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            >
              We'll use this to bring your order back next time.
            </p>
          </Field>

          <Field label="GST" testid="order-gst-field">
            <div className="flex gap-2">
              <Toggle
                active={gstRegistered === true}
                onClick={() => setGstRegistered(true)}
                testid="order-gst-registered"
              >
                Registered
              </Toggle>
              <Toggle
                active={gstRegistered === false}
                onClick={() => {
                  setGstRegistered(false);
                  setGstNumber("");
                }}
                testid="order-gst-unregistered"
              >
                Unregistered
              </Toggle>
            </div>
            {gstRegistered === true && (
              <>
                <input
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                  placeholder="15-character GSTIN"
                  maxLength={15}
                  data-testid="order-gstin-input"
                  className="mt-2 w-full text-sm bg-transparent outline-none px-3 py-2.5 rounded-sm border tracking-wider"
                  style={{
                    borderColor: gstinMissing && gstNumber ? "#C2410C" : "var(--mp-line)",
                    color: "var(--mp-text)",
                    fontFamily: "'Manrope', sans-serif",
                  }}
                />
                {gstinMissing && (
                  <p
                    className="mt-1 text-[11px]"
                    style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                    data-testid="order-gstin-hint"
                  >
                    Enter the full 15-character GSTIN, or pick Unregistered.
                  </p>
                )}
              </>
            )}
          </Field>

          <Field label="Anything to add" testid="order-notes-field">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Delivery preference, urgency, transport"
              data-testid="order-notes-input"
              className="w-full text-sm bg-transparent outline-none px-3 py-2.5 rounded-sm border resize-none"
              style={{
                borderColor: "var(--mp-line)",
                color: "var(--mp-text)",
                fontFamily: "'Manrope', sans-serif",
              }}
            />
          </Field>

          {error && (
            <p
              className="text-[11px]"
              style={{ color: "#C2410C", fontFamily: "'Manrope', sans-serif" }}
              data-testid="order-details-error"
            >
              {error}
            </p>
          )}

          <button
            onClick={() => send(true)}
            disabled={busy || gstinMissing}
            data-testid="order-details-submit"
            className="flex items-center justify-center gap-2 w-full text-sm font-medium py-3.5 rounded-sm transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{
              backgroundColor: "var(--mp-copper-solid)",
              color: "#FFFFFF",
              fontFamily: "'Manrope', sans-serif",
              opacity: gstinMissing ? 0.5 : undefined,
            }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            Send order on WhatsApp
          </button>

          <button
            onClick={() => send(false)}
            disabled={busy}
            data-testid="order-details-skip"
            className="w-full text-xs py-2 transition-opacity hover:opacity-70 disabled:opacity-60"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          >
            Skip and send without my details
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <p
      className="text-[10px] tracking-[0.16em] uppercase mb-1.5"
      style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
    >
      {label}
    </p>
    {children}
  </div>
);

const Toggle = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="flex-1 text-xs py-2.5 rounded-sm border transition-colors"
    style={{
      borderColor: active ? "var(--mp-copper)" : "var(--mp-line)",
      backgroundColor: active ? "var(--mp-copper-solid)" : "transparent",
      color: active ? "#FFFFFF" : "var(--mp-muted)",
      fontFamily: "'Manrope', sans-serif",
    }}
  >
    {children}
  </button>
);
